import React, { useState, useMemo, useEffect } from 'react'
import { Suggestion, SuggestionStatus } from '../../types/mockup'
import { SuggestionCard } from './SuggestionCard'
import { FilterBar } from './FilterBar'
import { ApplyConfirmationModal } from './ApplyConfirmationModal'
import { 
  useSuggestions, 
  useApproveSuggestion, 
  useRejectSuggestion 
} from '../../hooks/useSuggestions'
// Removed CSS import - using inline styles

interface SuggestionsDashboardProps {
  eventId: string
  onViewDetails: (suggestion: Suggestion) => void
  onQuickApply?: (suggestionId: string) => void
  onQuickDismiss?: (suggestionId: string) => void
}

type FilterType = 'all' | 'critical' | 'warning' | 'info' | 'dismissed'

export const SuggestionsDashboard: React.FC<SuggestionsDashboardProps> = ({
  eventId,
  onViewDetails,
  onQuickApply,
  onQuickDismiss
}) => {
  // State for filter and sort
  const [filter, setFilter] = useState<any>({
    riskLevels: [],
    statuses: ['pending'],
    showDismissed: false
  })
  const [sort, setSort] = useState<any>({
    field: 'urgency',
    direction: 'desc'
  })

  // Fetch suggestions from API
  const { data: suggestions = [], isLoading } = useSuggestions(eventId, filter)
  
  const approveMutation = useApproveSuggestion()
  const rejectMutation = useRejectSuggestion()
  
  const applySuggestion = (suggestionId: string) => {
    approveMutation.mutate({ eventId, suggestionId })
    if (onQuickApply) onQuickApply(suggestionId)
  }
    
  const dismissSuggestion = (suggestionId: string) => {
    rejectMutation.mutate({ eventId, suggestionId })
    if (onQuickDismiss) onQuickDismiss(suggestionId)
  }
  
  // Calculate stats
  const stats = {
    total: suggestions.length,
    pending: suggestions.filter((s: Suggestion) => s.status === 'pending').length,
    applied: suggestions.filter((s: Suggestion) => s.status === 'applied').length,
    dismissed: suggestions.filter((s: Suggestion) => s.status === 'dismissed').length,
  }
  
  // Debug logging
  console.log('Dashboard - All suggestions:', suggestions.length)
  console.log('Dashboard - Filtered suggestions:', suggestions.length)
  console.log('Dashboard - Current filter:', filter)
  console.log('Dashboard - First suggestion:', suggestions[0])

  const [activeFilter, setActiveFilter] = useState<FilterType>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [showApplyModal, setShowApplyModal] = useState(false)
  const [suggestionToApply, setSuggestionToApply] = useState<Suggestion | null>(null)

  // Set initial filter
  useEffect(() => {
    setActiveFilter('all')
  }, [])

  // Calculate counts for filter badges
  const filterCounts = useMemo(() => {
    const pendingSuggestions = suggestions.filter(s => s.status === 'pending')
    return {
      all: pendingSuggestions.length,
      critical: pendingSuggestions.filter(s => s.riskLevel === 'critical').length,
      warning: pendingSuggestions.filter(s => s.riskLevel === 'warning').length,
      info: pendingSuggestions.filter(s => s.riskLevel === 'info').length,
      dismissed: suggestions.filter(s => s.status === 'dismissed').length
    }
  }, [suggestions])

  // Calculate status counts for toggles
  const statusCounts = useMemo(() => {
    return {
      pending: suggestions.filter(s => s.status === 'pending').length,
      applied: suggestions.filter(s => s.status === 'applied').length,
      dismissed: suggestions.filter(s => s.status === 'dismissed').length,
      expired: suggestions.filter(s => s.status === 'expired').length
    }
  }, [suggestions])

  // Current status filter state
  const [statusFilter, setStatusFilter] = useState<SuggestionStatus | 'all'>('pending')

  // Handle status toggle clicks
  const handleStatusToggle = (status: SuggestionStatus | 'all') => {
    setStatusFilter(status)
    
    const newFilter = status === 'all' 
      ? {
          riskLevels: [],
          statuses: [],
          showDismissed: true
        }
      : {
          riskLevels: [],
          statuses: [status],
          showDismissed: status === 'dismissed'
        }
    
    setFilter(newFilter)
    
    // Reset the main filter to 'all' when using status toggles
    setActiveFilter('all')
  }

  // Handle filter changes - simple state update
  const handleFilterClick = (filterType: FilterType) => {
    console.log('Filter clicked:', filterType)
    setActiveFilter(filterType)
  }

  // Handle search changes by updating filter
  const handleSearchChange = (term: string) => {
    setSearchTerm(term)
    // Search functionality will be handled within the hook's filtering logic
  }

  // Simple filtered suggestions based on activeFilter state
  const filteredSuggestions = useMemo(() => {
    return suggestions.filter(suggestion => {
      // Apply search filter first
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        const matchesSearch = suggestion.headline.toLowerCase().includes(searchLower) ||
                            suggestion.description.toLowerCase().includes(searchLower)
        if (!matchesSearch) return false
      }

      // Then apply risk level filter
      if (activeFilter === 'all') return suggestion.status === 'pending'
      if (activeFilter === 'dismissed') return suggestion.status === 'dismissed'
      if (activeFilter === 'critical') return suggestion.riskLevel === 'critical' && suggestion.status === 'pending'
      if (activeFilter === 'warning') return suggestion.riskLevel === 'warning' && suggestion.status === 'pending'
      if (activeFilter === 'info') return suggestion.riskLevel === 'info' && suggestion.status === 'pending'
      return true
    })
  }, [suggestions, activeFilter, searchTerm])

  // Enhanced quick apply with confirmation modal
  const handleQuickApply = (suggestionId: string) => {
    const suggestion = suggestions.find(s => s.id === suggestionId)
    if (suggestion) {
      setSuggestionToApply(suggestion)
      setShowApplyModal(true)
    }
  }

  // Confirm apply action
  const handleConfirmApply = () => {
    if (suggestionToApply) {
      applySuggestion(suggestionToApply.id)
      onQuickApply?.(suggestionToApply.id)
      setShowApplyModal(false)
      setSuggestionToApply(null)
    }
  }

  // Cancel apply action
  const handleCancelApply = () => {
    setShowApplyModal(false)
    setSuggestionToApply(null)
  }

  // Enhanced quick dismiss with hook integration
  const handleQuickDismiss = (suggestionId: string) => {
    dismissSuggestion(suggestionId)
    onQuickDismiss?.(suggestionId)
  }

  // Empty state messages
  const getEmptyMessage = () => {
    if (searchTerm) {
      return `No suggestions match "${searchTerm}"`
    }
    if (activeFilter === 'dismissed') {
      return 'No dismissed suggestions'
    }
    if (activeFilter !== 'all') {
      return `No ${activeFilter} level suggestions`
    }
    return 'No active suggestions'
  }

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
      borderRadius: '16px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px 16px 24px',
        background: 'linear-gradient(135deg, #2e7d32 0%, #388e3c 100%)',
        color: 'white',
        borderBottom: '1px solid rgba(255,255,255,0.1)',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '12px',
            background: 'rgba(255,255,255,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px'
          }}>
            ✨
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: 600 }}>AI Suggestions</h3>
            <div style={{ fontSize: '14px', opacity: 0.9 }}>Intelligent schedule optimizations & recommendations</div>
          </div>
        </div>
        
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: '20px 24px' }}>
        {/* Filter Bar */}
        <div style={{
          padding: '16px 20px',
          background: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '20px'
        }}>
          <FilterBar
            activeFilter={activeFilter}
            filterCounts={filterCounts}
            onFilterClick={handleFilterClick}
            searchTerm={searchTerm}
            onSearchChange={handleSearchChange}
          />
        </div>

        {/* Suggestions List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
          {/* Render filtered suggestions */}
          {filteredSuggestions.map(suggestion => (
            <div key={suggestion.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '20px',
              marginBottom: '12px',
              background: 'white',
              borderRadius: '12px',
              border: `2px solid ${suggestion.riskLevel === 'critical' ? '#d93025' : suggestion.riskLevel === 'warning' ? '#ea8600' : '#2e7d32'}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.2s',
              cursor: 'pointer'
            }}>
              <div style={{ width: '60px', textAlign: 'center', marginRight: '16px' }}>
                <div style={{ fontSize: '24px', marginBottom: '4px' }}>
                  {suggestion.riskLevel === 'critical' ? '🚨' : 
                   suggestion.riskLevel === 'warning' ? '⚠️' : '💡'}
                </div>
                <div style={{
                  fontSize: '10px',
                  fontWeight: 'bold',
                  color: suggestion.riskLevel === 'critical' ? '#d93025' : suggestion.riskLevel === 'warning' ? '#ea8600' : '#2e7d32',
                  textTransform: 'uppercase'
                }}>
                  {suggestion.riskLevel}
                </div>
              </div>
              <div style={{ flex: 1, marginRight: '16px' }}>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>
                  {suggestion.headline}
                </h3>
                <p style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#666' }}>
                  {suggestion.description}
                </p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    background: suggestion.confidence >= 0.8 ? '#e8f5e9' : suggestion.confidence >= 0.6 ? '#fff3e0' : '#ffebee',
                    color: suggestion.confidence >= 0.8 ? '#2e7d32' : suggestion.confidence >= 0.6 ? '#f57c00' : '#c62828'
                  }}>
                    {suggestion.confidence >= 0.8 ? 'High Certainty' : 
                     suggestion.confidence >= 0.6 ? 'Needs Review' : 'Low Certainty'}
                  </span>
                  {suggestion.status !== 'pending' && (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: suggestion.status === 'applied' ? '#e8f5e9' : suggestion.status === 'dismissed' ? '#ffebee' : '#f5f5f5',
                      color: suggestion.status === 'applied' ? '#2e7d32' : suggestion.status === 'dismissed' ? '#c62828' : '#666'
                    }}>
                      {suggestion.status}
                    </span>
                  )}
                  {suggestion.impact.guestsAffected > 0 && (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: '#f5f5f5',
                      color: '#666'
                    }}>
                      {suggestion.impact.guestsAffected} guests affected
                    </span>
                  )}
                  {suggestion.operationalImpact.affectedVendors.length > 0 && (
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      fontSize: '12px',
                      background: '#f5f5f5',
                      color: '#666'
                    }}>
                      {suggestion.operationalImpact.affectedVendors.length} vendors
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {suggestion.status === 'pending' && suggestion.confidence >= 0.8 && (
                  <>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleQuickApply(suggestion.id)
                      }}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '4px',
                        border: 'none',
                        background: '#2e7d32',
                        color: 'white',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Apply
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleQuickDismiss(suggestion.id)
                      }}
                      style={{
                        padding: '8px 20px',
                        borderRadius: '4px',
                        border: '1px solid #ddd',
                        background: 'white',
                        color: '#666',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Dismiss
                    </button>
                  </>
                )}
                <button 
                  onClick={() => onViewDetails(suggestion)}
                  style={{
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd',
                    background: 'white',
                    color: '#666',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  Details →
                </button>
              </div>
            </div>
          ))}
          
          {filteredSuggestions.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              color: '#6b7280'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>
                {activeFilter === 'dismissed' ? '📦' : '✨'}
              </div>
              <h3 style={{ marginBottom: '8px', color: '#374151', fontSize: '20px', fontWeight: 500 }}>
                {activeFilter === 'all' ? 'No active suggestions' : 
                 activeFilter === 'dismissed' ? 'No dismissed suggestions' :
                 `No ${activeFilter} level suggestions`}
              </h3>
              <p style={{ margin: '0', fontSize: '14px', maxWidth: '400px', marginLeft: 'auto', marginRight: 'auto' }}>
                {activeFilter === 'all' ? 'All systems operating smoothly. Check back later for new suggestions.' :
                 activeFilter === 'dismissed' ? 'Dismissed suggestions will appear here for reference.' :
                 `No suggestions require ${activeFilter}-level attention.`}
              </p>
            </div>
          )}

        </div>

        {/* Apply Confirmation Modal */}
        {suggestionToApply && (
          <ApplyConfirmationModal
            suggestion={suggestionToApply}
            isOpen={showApplyModal}
            onConfirm={handleConfirmApply}
            onCancel={handleCancelApply}
          />
        )}
      </div>
    </div>
  )
}

export default SuggestionsDashboard